import React, { useState, useEffect } from "react";
import { Card, CardBody, Col, Button, Spinner, Container, Input, Modal, Nav, NavItem, NavLink, TabContent, TabPane } from "reactstrap";
import DataTable from "react-data-table-component";
import Breadcrumbs from "../../CommonElements/Breadcrumbs";
import { baseUrl, apiGet, apiPost, mediaUrl } from "../../Utils";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Btn, H3, H5 } from "../../AbstractElements";
import { useNavigate } from "react-router-dom";
import Swal from 'sweetalert2';
import { Edit, Plus, Trash2, Eye, MapPin, User, Mail, Phone } from 'react-feather';
import { useAuth } from "../../Context/AuthContext";

// Define Interface for Branch
interface BranchData {
  id: number;
  branch_name: string;
  address: string;
  email: string;
  phone: string;
  status: string;
  manager_name: string;
  manager_contact?: string;
  manager_email?: string;
  branch_image: string | null;
}

export default function ViewBranch() {
  const [data, setData] = useState<BranchData[]>([]);
  const [loading, setLoading] = useState(false);
  const [totalRows, setTotalRows] = useState(0);
  const [perPage, setPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [refresh, setRefresh] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortColumn, setSortColumn] = useState("");
  const [sortDirection, setSortDirection] = useState("");
  const prevSearch = React.useRef(searchTerm);
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const [modal, setModal] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [selectedBranch, setSelectedBranch] = useState<BranchData | null>(null);
  const [activeTab, setActiveTab] = useState('1');

  const toggleModal = () => {
    if (modal) {
      handleClose();
    } else {
      setModal(true);
    }
  };

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      setModal(false);
      setIsClosing(false);
    }, 550);
  };

  const toggleTab = (tab: string) => {
    if (activeTab !== tab) setActiveTab(tab);
  };

  const handleView = (branch: BranchData) => {
    setSelectedBranch(branch);
    setActiveTab('1');
    setModal(true);
  };

  useEffect(() => {
    const isSearchChanged = prevSearch.current !== searchTerm;
    prevSearch.current = searchTerm;

    // Reset to page 1 when search changes
    if (isSearchChanged && currentPage !== 1) {
      setCurrentPage(1);
      return; // Exit early, let page change trigger next fetch
    }

    const delay = isSearchChanged ? 500 : 0;
    const timer = setTimeout(() => {
      fetchData(currentPage, perPage, searchTerm, sortColumn, sortDirection);
    }, delay);
    return () => clearTimeout(timer);
  }, [refresh, currentPage, perPage, searchTerm, sortColumn, sortDirection]);

  const fetchData = async (page: number, size: number, search: string = "", sortCol: string = "", sortDir: string = "") => {
    setLoading(true);
    try {
      const body: any = {
        page: page,
        per_page: size,
        search: search
      };

      if (sortCol) {
        body.sort_by = sortCol;
        body.sort_direction = sortDir;
      }

      // API now returns pagination object: { data: [], total: ... }
      const response: any = await apiPost("branches/getall", body);
      if (response && response.data) {
        setData(response.data);
        setTotalRows(response.total);
      } else {
        setData(response);
        setTotalRows(response.length);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (column: any, sortDir: string) => {
    if (column.sortField) {
      setSortColumn(column.sortField);
      setSortDirection(sortDir);
      setCurrentPage(1);
    }
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handlePerRowsChange = async (newPerPage: number, page: number) => {
    setPerPage(newPerPage);
    setCurrentPage(page);
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  const handleAdd = () => {
    navigate(`${process.env.PUBLIC_URL}/add_branch`);
  };

  const handleEdit = (id: number) => {
    navigate(`${process.env.PUBLIC_URL}/edit_branch`, { state: { id: id } });
  };

  const handleDelete = async (id: number) => {
    Swal.fire({
      title: 'Are you sure?',
      text: "You won't be able to revert this!",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes, delete it!'
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await apiPost("branches/delete", { id: id });
          toast.success("Branch has been deleted successfully", { autoClose: 1000 });
          setRefresh(prev => prev + 1);
        } catch (error) {
          toast.error("Failed to delete branch", { autoClose: 1000 });
        }
      }
    });
  };

  const columns = [
    {
      name: <div>Sl. No</div>,
      selector: (row: BranchData, index?: number) => (currentPage - 1) * perPage + (index || 0) + 1,
      width: "80px",
    },
    {
      name: <div>Branch Name</div>,
      selector: (row: BranchData) => row.branch_name,
      sortable: true,
      sortField: "branch_name",
      wrap: true,
    },
    {
      name: <div>Address</div>,
      selector: (row: BranchData) => row.address,
      sortable: true,
      sortField: "address",
      wrap: true,
    },
    {
      name: <div>Manager</div>,
      selector: (row: BranchData) => row.manager_name || 'N/A',
      sortable: true,
      sortField: "manager_name",
      wrap: true,
    },
    {
      name: <div>Email</div>,
      selector: (row: BranchData) => row.email,
      sortable: true,
      sortField: "email",
      wrap: true,
    },
    {
      name: <div>Phone</div>,
      selector: (row: BranchData) => row.phone,
      sortable: true,
      sortField: "phone",
      wrap: true,
    },
    {
      name: "Status",
      cell: (row: BranchData) => (
        <span className={`badge ${row.status === 'Active' ? 'bg-success' : 'bg-secondary'}`}>
          {row.status}
        </span>
      ),
      sortable: true,
      sortField: "status",
    },
    {
      name: "Actions",
      cell: (row: BranchData) => (
        <div className="d-flex justify-content-between gap-2">
          {hasPermission('branch.view') && (
            <Button size="sm" color="primary" onClick={() => handleView(row)} title="View Details">
              <Eye size={14} />
            </Button>
          )}
          {hasPermission('branch.edit') && (
            <Button size="sm" color="info" onClick={() => handleEdit(row.id)} title="Edit">
              <Edit size={14} />
            </Button>
          )}
          {hasPermission('branch.delete') && (
            <Button size="sm" color="danger" onClick={() => row.id && handleDelete(row.id)} title="Delete">
              <Trash2 size={14} />
            </Button>
          )}
        </div>
      ),
      ignoreRowClick: true,
      allowOverflow: true,
    },
  ];

  /* 
  loading spinner handled in render, but react-data-table-component has a progressPending prop
  that is cleaner.
  */

  const customStyles = {
    headRow: {
      style: {
        backgroundColor: '#e0f2f1', // Light teal background
        color: '#00695c', // Dark teal text
        minHeight: '50px',
        borderBottom: '1px solid #e0e0e0',
      },
    },
    headCells: {
      style: {
        fontSize: '15px',
        fontWeight: '600',
        paddingLeft: '16px',
        paddingRight: '16px',
      },
    },
    rows: {
      style: {
        minHeight: '50px', // override the row height
        '&:not(:last-of-type)': {
          borderBottomStyle: 'solid',
          borderBottomWidth: '1px',
          borderBottomColor: '#e0e0e0',
        },
      },
    },
  };

  return (
    <div className='page-body'>
      <Breadcrumbs
        pageTitle="View Branch"
        parent="General"
        title="View Branch"
      />
      <Container fluid>
        <Col xl={12} className="mb-lg-3">
          <Card>
            <CardBody>
              <Col xs={12} className="mt-2 d-flex justify-content-between align-items-center mb-3">
                <H3 className="text-primary">Branch Overview</H3>
                {hasPermission('branch.add') && (
                  <Btn color="primary" onClick={handleAdd}>
                    <Plus size={16} /> Add Branch
                  </Btn>
                )}
              </Col>

              <Col xs={12} className="mb-3 d-flex justify-content-end">
                <Input type="text" placeholder="Search..." value={searchTerm} onChange={handleSearchChange} style={{ maxWidth: "300px" }} />
              </Col>
              <div className="table-responsive">
                <DataTable
                  data={data}
                  columns={columns}
                  pagination
                  paginationServer
                  paginationTotalRows={totalRows}
                  onChangePage={handlePageChange}
                  onChangeRowsPerPage={handlePerRowsChange}
                  onSort={handleSort}
                  sortServer
                  progressPending={loading}
                  progressComponent={<div className="d-flex justify-content-center align-items-center w-100" style={{ minHeight: '50vh' }}><Spinner color="primary" /></div>}
                  highlightOnHover
                  customStyles={customStyles}
                />
              </div>

            </CardBody>
          </Card>
        </Col>
      </Container>

      {/* View Branch Modal */}
      <Modal isOpen={modal} toggle={toggleModal} size="lg" centered contentClassName={isClosing ? "animate-3d-slit-out" : "animate-3d-slit"} fade={false}>
        <div style={{
          background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)',
          padding: '1.5rem',
          position: 'relative', width: '100%'
        }}>
          <button
            className="btn-close btn-close-white"
            type="button"
            onClick={handleClose}
            style={{ position: 'absolute', top: '1rem', right: '1rem', opacity: 0.9 }}
          ></button>

          {selectedBranch && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '1.5rem' }}>
              <div style={{
                width: '100px',
                height: '100px',
                borderRadius: '12px',
                overflow: 'hidden',
                border: '3px solid rgba(255,255,255,0.3)',
                boxShadow: '0 8px 24px rgba(0,0,0,0.3)',
                flexShrink: 0,
                backgroundColor: '#f8f9fa',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center'
              }}>
                {selectedBranch.branch_image ? (
                  <img
                    src={`${mediaUrl}${selectedBranch.branch_image}`}
                    alt="Branch"
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    onError={(e) => {
                      (e.target as HTMLImageElement).src = 'https://via.placeholder.com/100/10b981/ffffff?text=Branch';
                    }}
                  />
                ) : (
                  <MapPin size={40} color="white" />
                )}
              </div>
              <div style={{ flex: 1 }}>
                <h3 style={{
                  color: 'white',
                  fontWeight: 700,
                  fontSize: '1.75rem',
                  margin: 0,
                  marginBottom: '0.5rem'
                }}>
                  {selectedBranch.branch_name}
                </h3>
                <div style={{ display: 'flex', gap: '1rem', color: 'rgba(255,255,255,0.85)', fontSize: '0.9rem' }}>
                  <span><MapPin size={14} className="me-1" />{selectedBranch.address}</span>
                </div>
              </div>
              <div>
                <span style={{
                  backgroundColor: selectedBranch.status === 'Active' ? 'rgba(255,255,255,0.3)' : 'rgba(255,255,255,0.2)',
                  color: 'white',
                  padding: '0.6rem 1.2rem',
                  borderRadius: '25px',
                  fontSize: '0.875rem',
                  fontWeight: 700,
                  border: '2px solid rgba(255,255,255,0.5)',
                  backdropFilter: 'blur(10px)'
                }}>
                  {selectedBranch.status}
                </span>
              </div>
            </div>
          )}
        </div>

        <div className="modal-body p-0">
          <Nav tabs className="nav-primary" style={{ padding: '0 1.5rem', marginTop: '1rem', borderBottom: '1px solid #dee2e6' }}>
            <NavItem>
              <NavLink
                className={activeTab === '1' ? 'active' : ''}
                onClick={() => toggleTab('1')}
                style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px', padding: '12px 20px' }}
              >
                <MapPin size={16} /> Branch Info
              </NavLink>
            </NavItem>
            <NavItem>
              <NavLink
                className={activeTab === '2' ? 'active' : ''}
                onClick={() => toggleTab('2')}
                style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px', padding: '12px 20px' }}
              >
                <User size={16} /> Manager Details
              </NavLink>
            </NavItem>
            <NavItem>
              <NavLink
                className={activeTab === '3' ? 'active' : ''}
                onClick={() => toggleTab('3')}
                style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px', padding: '12px 20px' }}
              >
                <Phone size={16} /> Contact Info
              </NavLink>
            </NavItem>
          </Nav>

          <div className="p-4" style={{ backgroundColor: '#f8f9fa', minHeight: '300px' }}>
            {selectedBranch && (
              <TabContent activeTab={activeTab}>
                {/* Tab 1: Branch Information */}
                <TabPane tabId="1">
                  <div className="bg-white p-4 rounded shadow-sm">
                    <div className="row g-4">
                      <div className="col-md-6">
                        <div className="mb-3">
                          <label className="text-muted small fw-bold text-uppercase">Branch Name</label>
                          <div className="fw-bold fs-6 text-primary">{selectedBranch.branch_name}</div>
                        </div>
                        <div className="mb-3">
                          <label className="text-muted small fw-bold text-uppercase">Status</label>
                          <div>
                            <span className={`badge ${selectedBranch.status === 'Active' ? 'bg-success' : 'bg-secondary'}`}>
                              {selectedBranch.status}
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div className="mb-3">
                          <label className="text-muted small fw-bold text-uppercase">Address</label>
                          <div className="fw-semibold">{selectedBranch.address}</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </TabPane>

                {/* Tab 2: Manager Details */}
                <TabPane tabId="2">
                  <div className="bg-white p-4 rounded shadow-sm">
                    <div className="row g-4">
                      <div className="col-md-6">
                        <div className="mb-3">
                          <label className="text-muted small fw-bold text-uppercase">Manager Name</label>
                          <div className="fw-bold fs-6 text-primary">
                            {selectedBranch.manager_name || <span className="text-muted">Not Assigned</span>}
                          </div>
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div className="mb-3">
                          <label className="text-muted small fw-bold text-uppercase">Manager Contact</label>
                          <div className="fw-semibold">
                            {selectedBranch.manager_contact ? (
                              <a href={`tel:${selectedBranch.manager_contact}`} className="text-decoration-none">
                                {selectedBranch.manager_contact}
                              </a>
                            ) : <span className="text-muted">Not Available</span>}
                          </div>
                        </div>
                        <div className="mb-3">
                          <label className="text-muted small fw-bold text-uppercase">Manager Email</label>
                          <div className="fw-semibold">
                            {selectedBranch.manager_email ? (
                              <a href={`mailto:${selectedBranch.manager_email}`} className="text-decoration-none">
                                {selectedBranch.manager_email}
                              </a>
                            ) : <span className="text-muted">Not Available</span>}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </TabPane>

                {/* Tab 3: Contact Information */}
                <TabPane tabId="3">
                  <div className="bg-white p-4 rounded shadow-sm">
                    <div className="row g-4">
                      <div className="col-md-6">
                        <div className="mb-4">
                          <label className="text-muted small fw-bold text-uppercase d-flex align-items-center gap-2 mb-2">
                            <Mail size={14} /> Email Address
                          </label>
                          <a href={`mailto:${selectedBranch.email}`} className="fw-semibold fs-6 text-decoration-none">
                            {selectedBranch.email}
                          </a>
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div className="mb-4">
                          <label className="text-muted small fw-bold text-uppercase d-flex align-items-center gap-2 mb-2">
                            <Phone size={14} /> Phone Number
                          </label>
                          <a href={`tel:${selectedBranch.phone}`} className="fw-semibold fs-6 text-decoration-none">
                            {selectedBranch.phone}
                          </a>
                        </div>
                      </div>
                      <div className="col-12">
                        <div>
                          <label className="text-muted small fw-bold text-uppercase d-flex align-items-center gap-2 mb-2">
                            <MapPin size={14} /> Full Address
                          </label>
                          <div className="fw-semibold fs-6 p-3 bg-light rounded text-dark" style={{ minHeight: '80px' }}>
                            {selectedBranch.address}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </TabPane>
              </TabContent>
            )}
          </div>
        </div>
      </Modal>

      <ToastContainer />
    </div>
  );
}
